// services/credential.service.js
import axios from 'axios';
import AuthService from './auth.service';

/**
 * Credential Service - Servicio completo para gestión de credenciales
 * 
 * Tipos de credenciales soportados:
 * - relational_database: host, port, database_name, username, password
 * - uri_database: connection_uri
 * - provider: api_key, provider_id, api_endpoint (opcional)
 * - api: para APIs genéricas
 */

class CredentialService {
  constructor() {
    // Base URL del Credential Service
    this.baseURL = process.env.REACT_APP_CREDENTIAL_API_URL || 'http://localhost:8001';
    
    // API Key requerida para el Credential Service
    this.apiKey = process.env.REACT_APP_CREDENTIAL_API_KEY || '';
    
    // Crear instancia de axios
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-api-key': this.apiKey,
      },
    });

    // Interceptor para agregar headers de autenticación
    this.client.interceptors.request.use(
      (config) => {
        const token = AuthService.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Agregar x-account-id si está disponible en localStorage
        const accountId = localStorage.getItem('currentAccountId');
        if (accountId) {
          config.headers['x-account-id'] = accountId;
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar respuestas y errores
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('🔒 Token expirado o inválido');
          AuthService.logout();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Actualizar la base URL del servicio
   * @param {string} url - Nueva base URL
   */
  setBaseURL(url) {
    this.baseURL = url;
    this.client.defaults.baseURL = url;
  }

  /**
   * Helper para manejar errores de API
   * @param {Error} error - Error de axios
   * @returns {Object} Objeto de error normalizado
   */
  handleError(error) {
    const errorResponse = {
      message: 'An unexpected error occurred',
      status: 500,
      code: 'UNKNOWN_ERROR',
    };

    if (error.response) {
      errorResponse.message = error.response.data?.message || error.response.data?.error || error.message;
      errorResponse.status = error.response.status;
      errorResponse.code = error.response.data?.code || `HTTP_${error.response.status}`;
    } else if (error.request) {
      errorResponse.message = 'No response from server';
      errorResponse.code = 'NETWORK_ERROR';
    } else {
      errorResponse.message = error.message;
    }

    console.error('❌ Credential API Error:', errorResponse);
    return errorResponse;
  }

  /* ========================================================================== */
  /*  CRUD - CREDENCIALES                                                       */
  /* ========================================================================== */

  /**
   * 1️⃣ Crear Credencial
   * POST /credential
   * 
   * @param {Object} credential - Datos de la credencial
   * @param {string} credential.name - Nombre de la credencial
   * @param {string} credential.description - Descripción opcional
   * @param {string} credential.credential_type - Tipo: 'relational_database' | 'uri_database' | 'provider' | 'api'
   * @param {string} [credential.host] - Host (para relational_database)
   * @param {number} [credential.port] - Puerto (para relational_database)
   * @param {string} [credential.database_name] - Nombre de la base de datos (para relational_database)
   * @param {string} [credential.username] - Usuario (para relational_database)
   * @param {string} [credential.password] - Contraseña (para relational_database)
   * @param {string} [credential.connection_uri] - URI de conexión (para uri_database)
   * @param {string} [credential.api_key] - API Key (para provider)
   * @param {string} [credential.provider_name] - Nombre del proveedor (para provider)
   * @param {string} [credential.api_endpoint] - Endpoint opcional (para provider)
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async createCredential(credential) {
    try {
      const response = await this.client.post('/credential', { credential });
      return { 
        success: true, 
        data: response.data.credential || response.data.body || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 2️⃣ Obtener Credencial por tipo e ID
   * GET /credential/{credential_type}/{credential_id}
   * 
   * @param {string} credentialType - Tipo de credencial
   * @param {string} credentialId - UUID de la credencial
   * @param {string} [accountId] - UUID de la cuenta (opcional, usa header si no se provee)
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async getCredential(credentialType, credentialId, accountId = null) {
    try {
      const params = accountId ? { account_id: accountId } : {};
      const response = await this.client.get(`/credential/${credentialType}/${credentialId}`, { params });
      return { 
        success: true, 
        data: response.data.credential || response.data.body || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 3️⃣ Listar Credenciales por Tipo
   * GET /credential/{credential_type}
   * 
   * @param {string} credentialType - Tipo de credencial: 'relational_database' | 'uri_database' | 'provider' | 'api'
   * @returns {Promise<Object>} { success: boolean, data?: Array, error?: Object }
   */
  async listCredentialsByType(credentialType) {
    try {
      const response = await this.client.get(`/credential/${credentialType}`);
      const credentials = response.data.credentials || response.data.body?.credentials || response.data.body || response.data;
      return { 
        success: true, 
        data: Array.isArray(credentials) ? credentials : [] 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 4️⃣ Actualizar Credencial
   * PUT /credential/{credential_type}/{credential_id}
   * 
   * @param {string} credentialType - Tipo de credencial
   * @param {string} credentialId - UUID de la credencial
   * @param {Object} credential - Campos a actualizar
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async updateCredential(credentialType, credentialId, credential) {
    try {
      const response = await this.client.put(`/credential/${credentialType}/${credentialId}`, { credential });
      return { 
        success: true, 
        data: response.data.credential || response.data.body || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 5️⃣ Eliminar Credencial
   * DELETE /credential/{credential_type}/{credential_id}
   * 
   * @param {string} credentialType - Tipo de credencial
   * @param {string} credentialId - UUID de la credencial
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async deleteCredential(credentialType, credentialId) {
    try {
      const response = await this.client.delete(`/credential/${credentialType}/${credentialId}`);
      return { 
        success: true, 
        data: response.data.message || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /* ========================================================================== */
  /*  ASIGNACIONES DE CREDENCIALES                                              */
  /* ========================================================================== */

  /**
   * 6️⃣ Asignar Credencial a Usuario
   * POST /credential/assignments
   * 
   * @param {string} credentialId - UUID de la credencial
   * @param {string} userId - UUID del usuario destino
   * @param {string} accountId - UUID de la cuenta/organización
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async assignCredentialToUser(credentialId, userId, accountId) {
    try {
      const response = await this.client.post('/credential/assignments', {
        credential_id: credentialId,
        user_id: userId,
        account_id: accountId,
      });
      return { 
        success: true, 
        data: response.data.assignment || response.data.body || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 7️⃣ Obtener Asignación
   * GET /credential/assignments/{assignment_id}
   * 
   * @param {string} assignmentId - UUID de la asignación
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async getAssignment(assignmentId) {
    try {
      const response = await this.client.get(`/credential/assignments/${assignmentId}`);
      return { 
        success: true, 
        data: response.data.assignment || response.data.body || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 8️⃣ Listar Credenciales Asignadas a Usuario
   * GET /credential/assignments/user/{user_id}
   * 
   * @param {string} userId - UUID del usuario
   * @param {Object} [options] - Opciones de filtrado
   * @param {string} [options.accountId] - UUID de la cuenta
   * @param {boolean} [options.activeOnly=true] - Solo asignaciones activas
   * @returns {Promise<Object>} { success: boolean, data?: Array, error?: Object }
   */
  async listUserAssignments(userId, options = {}) {
    try {
      const { accountId, activeOnly = true } = options;
      
      const params = { active_only: activeOnly };
      if (accountId) params.account_id = accountId;
      
      const response = await this.client.get(`/credential/assignments/user/${userId}`, { params });
      const assignments = response.data.assignments || response.data.body?.assignments || response.data.body || response.data;
      return { 
        success: true, 
        data: Array.isArray(assignments) ? assignments : [] 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 9️⃣ Listar Usuarios con Acceso a Credencial
   * GET /credential/assignments/credential/{credential_id}
   * 
   * @param {string} credentialId - UUID de la credencial
   * @param {boolean} [activeOnly=true] - Solo asignaciones activas
   * @returns {Promise<Object>} { success: boolean, data?: Array, error?: Object }
   */
  async listCredentialUsers(credentialId, activeOnly = true) {
    try {
      const response = await this.client.get(`/credential/assignments/credential/${credentialId}`, {
        params: { active_only: activeOnly }
      });
      const users = response.data.users || response.data.assignments || response.data.body || response.data;
      return { 
        success: true, 
        data: Array.isArray(users) ? users : [] 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 🔟 Listar Asignaciones de la Organización
   * GET /credential/assignments/organization
   * 
   * @param {boolean} [activeOnly=true] - Solo asignaciones activas
   * @returns {Promise<Object>} { success: boolean, data?: Array, error?: Object }
   */
  async listOrganizationAssignments(activeOnly = true) {
    try {
      const response = await this.client.get('/credential/assignments/organization', {
        params: { active_only: activeOnly }
      });
      const assignments = response.data.assignments || response.data.body?.assignments || response.data.body || response.data;
      return { 
        success: true, 
        data: Array.isArray(assignments) ? assignments : [] 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 1️⃣1️⃣ Revocar Asignación
   * DELETE /credential/assignments/revoke
   * 
   * @param {string} credentialId - UUID de la credencial
   * @param {string} userId - UUID del usuario
   * @param {string} accountId - UUID de la cuenta
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async revokeAssignment(credentialId, userId, accountId) {
    try {
      const response = await this.client.delete('/credential/assignments/revoke', {
        data: {
          credential_id: credentialId,
          user_id: userId,
          account_id: accountId,
        }
      });
      return { 
        success: true, 
        data: response.data.message || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 1️⃣2️⃣ Suspender Asignación
   * PUT /credential/assignments/suspend
   * 
   * @param {string} credentialId - UUID de la credencial
   * @param {string} userId - UUID del usuario
   * @param {string} accountId - UUID de la cuenta
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async suspendAssignment(credentialId, userId, accountId) {
    try {
      const response = await this.client.put('/credential/assignments/suspend', {
        credential_id: credentialId,
        user_id: userId,
        account_id: accountId,
      });
      return { 
        success: true, 
        data: response.data.assignment || response.data.body || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 1️⃣3️⃣ Reactivar Asignación
   * PUT /credential/assignments/reactivate
   * 
   * @param {string} credentialId - UUID de la credencial
   * @param {string} userId - UUID del usuario
   * @param {string} accountId - UUID de la cuenta
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async reactivateAssignment(credentialId, userId, accountId) {
    try {
      const response = await this.client.put('/credential/assignments/reactivate', {
        credential_id: credentialId,
        user_id: userId,
        account_id: accountId,
      });
      return { 
        success: true, 
        data: response.data.assignment || response.data.body || response.data 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /**
   * 1️⃣4️⃣ Auditoría de Asignaciones
   * GET /credential/assignments/audit/{assigned_by}
   * 
   * @param {string} assignedBy - UUID del usuario que hizo las asignaciones
   * @returns {Promise<Object>} { success: boolean, data?: Array, error?: Object }
   */
  async getAssignmentAudit(assignedBy) {
    try {
      const response = await this.client.get(`/credential/assignments/audit/${assignedBy}`);
      const audit = response.data.assignments || response.data.audit || response.data.body || response.data;
      return { 
        success: true, 
        data: Array.isArray(audit) ? audit : [] 
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }

  /* ========================================================================== */
  /*  MÉTODOS DE CONVENIENCIA                                                   */
  /* ========================================================================== */

  /**
   * Listar todas las credenciales de base de datos relacionales
   * @returns {Promise<Object>}
   */
  async listRelationalDatabases() {
    return this.listCredentialsByType('relational_database');
  }

  /**
   * Listar todas las credenciales de base de datos URI
   * @returns {Promise<Object>}
   */
  async listUriDatabases() {
    return this.listCredentialsByType('uri_database');
  }

  /**
   * Listar todas las credenciales de proveedores (API keys)
   * @returns {Promise<Object>}
   */
  async listProviders() {
    return this.listCredentialsByType('provider');
  }

  /**
   * Listar credenciales de APIs externas (base_url + auth), usadas por el step api_call
   * @returns {Promise<Object>}
   */
  async listApis() {
    return this.listCredentialsByType('api');
  }

  /**
   * Crear credencial de base de datos relacional
   * @param {Object} data - { name, description, host, port, database_name, username, password }
   * @returns {Promise<Object>}
   */
  async createRelationalDatabase(data) {
    return this.createCredential({
      ...data,
      credential_type: 'relational_database',
    });
  }

  /**
   * Crear credencial de base de datos URI
   * @param {Object} data - { name, description, connection_uri, engine }
   * @returns {Promise<Object>}
   */
  async createUriDatabase(data) {
    return this.createCredential({
      credential_type: 'uri_database',
      name: data.name,
      description: data.description,
      connection_uri: data.connection_uri,
      engine: data.engine || 'mongodb', // Engine es requerido: mongodb, redis, etc.
    });
  }

  /**
   * Crear credencial de proveedor (API key)
   * @param {Object} data - { name, description, api_key, provider_id, api_endpoint }
   * @returns {Promise<Object>}
   */
  async createProvider(data) {
    // Asegurar que se usa provider_id (no provider_name)
    const credentialData = {
      credential_type: 'provider',
      provider_id: data.provider_id || data.provider_name, // Compatibilidad con código antiguo
      api_key: data.api_key,
      name: data.name,
      description: data.description,
    };
    
    // Agregar api_endpoint si está presente
    if (data.api_endpoint) {
      credentialData.api_endpoint = data.api_endpoint;
    }
    
    return this.createCredential(credentialData);
  }

  /**
   * Listar todas las credenciales (todos los tipos)
   * @returns {Promise<Object>} { success: boolean, data?: Object, error?: Object }
   */
  async listAllCredentials() {
    try {
      
      const [relational, uri, providers] = await Promise.all([
        this.listRelationalDatabases(),
        this.listUriDatabases(),
        this.listProviders(),
      ]);

      return {
        success: true,
        data: {
          relational_database: relational.success ? relational.data : [],
          uri_database: uri.success ? uri.data : [],
          provider: providers.success ? providers.data : [],
        }
      };
    } catch (error) {
      return { success: false, error: this.handleError(error) };
    }
  }
}

// Exportar instancia singleton
const credentialService = new CredentialService();
export default credentialService;

// También exportar la clase para casos donde se necesite una nueva instancia
export { CredentialService };
